import Phaser from 'phaser';
import type { PieceState } from '../../services/puzzleCore';
import {
  TRAY_TUNED_DIFFICULTIES,
  TRAY_HIRES_SCALE,
} from '../../config/constants';
import { getEdgeProfile } from './EdgeProfiles';
import type { EdgesGrid } from './JigsawEdgeGrid';

/**
 * Создание и удаление кусочных текстур (RenderTexture → draw → saveTexture).
 */
const DEBUG_EDGES = false;

/** Суффикс ключа текстуры для кусочков на доске (ослабленная тень — выглядят частью изображения). */
export const BOARD_PIECE_TEXTURE_SUFFIX = '-board';

type ShadowParams = {
  color: string;
  blur: number;
  offsetX: number;
  offsetY: number;
};

type EdgeInfoLocal = { type: 'flat' | 'in' | 'out'; variant: number };

function normalizeEdge(edge: EdgeInfoLocal | undefined): EdgeInfoLocal {
  if (!edge) return { type: 'flat', variant: 0 };
  const type = edge.type === 'in' || edge.type === 'out' || edge.type === 'flat' ? edge.type : 'flat';
  const variant = Number.isFinite(edge.variant) ? Math.trunc(edge.variant) : 0;
  return { type, variant };
}

function drawEdge(
  ctx: CanvasRenderingContext2D,
  fromX: number,
  fromY: number,
  toX: number,
  toY: number,
  edge: EdgeInfoLocal,
  tab: number,
  orientation: 'top' | 'right' | 'bottom' | 'left'
): void {
  if (edge.type === 'flat') {
    ctx.lineTo(toX, toY);
    return;
  }

  const profile = getEdgeProfile(edge.variant);
  if (!profile || !Array.isArray(profile.a) || !Array.isArray(profile.b)) {
    ctx.lineTo(toX, toY);
    return;
  }
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.max(1e-6, Math.sqrt(dx * dx + dy * dy));
  const tx = dx / len;
  const ty = dy / len;
  const edgeSign = edge.type === 'in' ? -1 : 1;

  const normal = (() => {
    switch (orientation) {
      case 'top':
        return { x: 0, y: -1 };
      case 'right':
        return { x: 1, y: 0 };
      case 'bottom':
        return { x: 0, y: 1 };
      case 'left':
        return { x: -1, y: 0 };
    }
  })();

  const mapPoint = (u: number, v: number): { x: number; y: number } => {
    const baseX = fromX + tx * (u * len);
    const baseY = fromY + ty * (u * len);
    const offset = (v - 0.5) * 2 * tab * edgeSign;
    return {
      x: baseX + normal.x * offset,
      y: baseY + normal.y * offset,
    };
  };

  const segs = [...profile.a, ...profile.b];
  for (const seg of segs) {
    const c1 = mapPoint(seg.cx1, seg.cy1);
    const c2 = mapPoint(seg.cx2, seg.cy2);
    const p = mapPoint(seg.x, seg.y);
    ctx.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, p.x, p.y);
  }

  // Завершаем путь строго в целевой точке, чтобы не копить ошибки округления.
  ctx.lineTo(toX, toY);
}

export const PieceTextureFactory = {
  /**
   * Нарезает исходную текстуру на текстуры по одной на деталь.
   * Возвращает массив ключей текстур (piece-{puzzleId}-{difficulty}-{piece.id}).
   */
  create(
    scene: Phaser.Scene,
    textureKey: string,
    pieces: PieceState[],
    puzzleId: string,
    difficulty: number,
    srcCellW: number,
    srcCellH: number,
    edgesGrid: EdgesGrid,
    tabRatio = 0.22
  ): string[] {
    const keys: string[] = [];
    const sourceImage = scene.textures.get(textureKey).getSourceImage() as CanvasImageSource;
    const safeTabRatio = Math.max(0, tabRatio);
    const hiResTrayDifficulties = new Set<number>(TRAY_TUNED_DIFFICULTIES as readonly number[]);
    const trayScale = hiResTrayDifficulties.has(difficulty) ? Math.max(1, TRAY_HIRES_SCALE) : 1;

    pieces.forEach((piece: PieceState) => {
      const pieceKey = `piece-${puzzleId}-${difficulty}-${piece.id}`;
      keys.push(pieceKey);

      const sx = piece.col * srcCellW;
      const sy = piece.row * srcCellH;
      const tab = Math.max(0, Math.round(Math.min(srcCellW, srcCellH) * safeTabRatio));
      const sourceDrawW = srcCellW + tab * 2;
      const sourceDrawH = srcCellH + tab * 2;
      const boardW = Math.max(1, Math.ceil(sourceDrawW));
      const boardH = Math.max(1, Math.ceil(sourceDrawH));
      const trayW = Math.max(1, Math.ceil(sourceDrawW * trayScale));
      const trayH = Math.max(1, Math.ceil(sourceDrawH * trayScale));
      const trayTab = tab * trayScale;
      const x0Board = tab;
      const y0Board = tab;
      const x1Board = tab + srcCellW;
      const y1Board = tab + srcCellH;
      const x0Tray = trayTab;
      const y0Tray = trayTab;
      const x1Tray = trayTab + srcCellW * trayScale;
      const y1Tray = trayTab + srcCellH * trayScale;
      const rawEdges = edgesGrid[piece.id] ?? {
        top: { type: 'flat' as const, variant: 0 },
        right: { type: 'flat' as const, variant: 0 },
        bottom: { type: 'flat' as const, variant: 0 },
        left: { type: 'flat' as const, variant: 0 },
      };
      const edges = {
        top: normalizeEdge(rawEdges.top),
        right: normalizeEdge(rawEdges.right),
        bottom: normalizeEdge(rawEdges.bottom),
        left: normalizeEdge(rawEdges.left),
      };

      const drawPiece = (
        ctx: CanvasRenderingContext2D,
        geom: { x0: number; y0: number; x1: number; y1: number; tab: number; w: number; h: number },
        shadow: ShadowParams,
        strokeWidth: number
      ): void => {
        const buildPath = () => {
          ctx.beginPath();
          ctx.moveTo(geom.x0, geom.y0);
          drawEdge(ctx, geom.x0, geom.y0, geom.x1, geom.y0, edges.top, geom.tab, 'top');
          drawEdge(ctx, geom.x1, geom.y0, geom.x1, geom.y1, edges.right, geom.tab, 'right');
          drawEdge(ctx, geom.x1, geom.y1, geom.x0, geom.y1, edges.bottom, geom.tab, 'bottom');
          drawEdge(ctx, geom.x0, geom.y1, geom.x0, geom.y0, edges.left, geom.tab, 'left');
          ctx.closePath();
        };

        ctx.save();
        buildPath();
        ctx.shadowColor = shadow.color;
        ctx.shadowBlur = shadow.blur;
        ctx.shadowOffsetX = shadow.offsetX;
        ctx.shadowOffsetY = shadow.offsetY;
        ctx.fillStyle = 'rgba(0,0,0,0.02)';
        ctx.fill();
        ctx.restore();

        ctx.save();
        buildPath();
        ctx.clip();
        ctx.drawImage(sourceImage, sx - tab, sy - tab, sourceDrawW, sourceDrawH, 0, 0, geom.w, geom.h);
        ctx.restore();

        ctx.save();
        buildPath();
        ctx.strokeStyle = 'rgba(20,20,20,0.42)';
        ctx.lineWidth = strokeWidth;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        ctx.stroke();
        ctx.restore();
      };

      const canvas = document.createElement('canvas');
      canvas.width = trayW;
      canvas.height = trayH;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      // Текстура для лотка: тень для объёма (выпуклые кусочки).
      const trayShadow: ShadowParams = {
        color: 'rgba(0,0,0,0.22)',
        blur: 4 * trayScale,
        offsetX: 0.75 * trayScale,
        offsetY: 1.25 * trayScale,
      };
      drawPiece(
        ctx,
        { x0: x0Tray, y0: y0Tray, x1: x1Tray, y1: y1Tray, tab: trayTab, w: trayW, h: trayH },
        trayShadow,
        2.25 * trayScale
      );
      if (DEBUG_EDGES) {
        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        ctx.fillRect(6, trayH / 2 - 24, Math.min(trayW - 12, 120), 48);
        ctx.fillStyle = '#ffffff';
        ctx.font = '11px sans-serif';
        ctx.textAlign = 'left';
        ctx.fillText(`T:${edges.top.type}:${edges.top.variant}`, 10, trayH / 2 - 10);
        ctx.fillText(`R:${edges.right.type}:${edges.right.variant}`, 10, trayH / 2 + 2);
        ctx.fillText(`B:${edges.bottom.type}:${edges.bottom.variant}`, 10, trayH / 2 + 14);
        ctx.fillText(`L:${edges.left.type}:${edges.left.variant}`, 10, trayH / 2 + 26);
        ctx.restore();
      }
      if (scene.textures.exists(pieceKey)) scene.textures.remove(pieceKey);
      scene.textures.addCanvas(pieceKey, canvas);

      // Текстура для доски: ослабленная тень — кусочек выглядит частью изображения.
      const boardKey = pieceKey + BOARD_PIECE_TEXTURE_SUFFIX;
      const boardCanvas = document.createElement('canvas');
      boardCanvas.width = boardW;
      boardCanvas.height = boardH;
      const boardCtx = boardCanvas.getContext('2d');
      if (boardCtx) {
        const boardShadow: ShadowParams = {
          color: 'rgba(0,0,0,0.10)',
          blur: 2,
          offsetX: 0.35,
          offsetY: 0.6,
        };
        drawPiece(
          boardCtx,
          { x0: x0Board, y0: y0Board, x1: x1Board, y1: y1Board, tab, w: boardW, h: boardH },
          boardShadow,
          2.25
        );
        if (scene.textures.exists(boardKey)) scene.textures.remove(boardKey);
        scene.textures.addCanvas(boardKey, boardCanvas);
      }

    });

    return keys;
  },

  /**
   * Удаляет кусочные текстуры из менеджера текстур сцены.
   */
  destroy(scene: Phaser.Scene, pieceTextureKeys: string[]): void {
    pieceTextureKeys.forEach((key) => {
      if (scene.textures.exists(key)) scene.textures.remove(key);
      const boardKey = key + BOARD_PIECE_TEXTURE_SUFFIX;
      if (scene.textures.exists(boardKey)) scene.textures.remove(boardKey);
    });
  },
};
